import { format as formatDate, startOfDay } from 'date-fns';
import type { ScheduleTaskForChart } from '../../../services/scheduleService';
import { getScheduleTasks } from '../../../services/scheduleService';
import { parseDateOnly } from './scheduleGanttMapping';
import { escapeHtml } from './schedulePrint';
import { formatScheduleTaskStatusLabel } from './scheduleTaskStatuses';

/**
 * Planned completion is required for this report (no invented end dates).
 * START uses identified / start date when present; otherwise START shows "—" in the table.
 */
export function resolveTaskPrintDates(task: ScheduleTaskForChart): { startDate: Date | null; endDate: Date } | null {
  const endRaw = parseDateOnly(String(task.plannedCompletionDate ?? '').trim());
  if (!endRaw) return null;

  const startRaw = parseDateOnly(String(task.identifiedDate ?? task.startDate ?? '').trim());
  return {
    startDate: startRaw ? startOfDay(startRaw) : null,
    endDate: startOfDay(endRaw),
  };
}

/** True when the planned interval overlaps the user-selected report range (inclusive calendar days). */
export function plannedWindowOverlapsReportRange(
  dates: { startDate: Date | null; endDate: Date },
  reportStart: Date,
  reportEnd: Date
): boolean {
  const rs = startOfDay(reportStart).getTime();
  const re = startOfDay(reportEnd).getTime();
  const intervalStart = startOfDay(dates.startDate ?? dates.endDate).getTime();
  const intervalEnd = startOfDay(dates.endDate).getTime();
  return intervalStart <= re && intervalEnd >= rs;
}

function fmtReportDate(d: Date): string {
  return formatDate(d, 'dd MMMM yyyy');
}

function fmtTableDate(d: Date | null): string {
  if (!d) return '—';
  return formatDate(d, 'dd MMM yyyy');
}

/** Same asset as server PDFs (`High-Res Logo.png`); loaded as data URL so print preview always resolves. */
async function loadDawsonRoadMaintenanceLogoDataUrl(): Promise<string | null> {
  try {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const href = `${window.location.origin}${base}${encodeURI('/High-Res Logo.png')}`;
    const res = await fetch(href, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(typeof fr.result === 'string' ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * QMS017-style Weekly Maintenance Schedule — opens a print preview window.
 */
export async function openWeeklyMaintenanceSchedulePrint(params: {
  serviceArea?: string;
  foremanArea: string;
  dateFrom: string;
  dateTo: string;
  /** Foreman area name / text (QMS017 manager area). */
  managerAreaName: string;
  /** Service area display name (from selected foreman’s service area). */
  serviceAreaName: string;
  /** User who generated the report. */
  scheduledBy: string;
  /** Report “date scheduled”; defaults to now. */
  generatedAt?: Date;
}): Promise<void> {
  const {
    serviceArea,
    foremanArea,
    dateFrom,
    dateTo,
    managerAreaName,
    serviceAreaName,
    scheduledBy,
    generatedAt = new Date(),
  } = params;

  /** Omit workType so the report includes Quantified and Routine (same idea as Task Schedule “All”). */
  const tasks = await getScheduleTasks({
    dateFrom,
    dateTo,
    serviceArea: serviceArea || undefined,
    foremanArea: foremanArea || undefined,
  });

  /** Exclude schedule “ongoing / year-round” filler rows (hardcoded templates), same as excluding them from this report. */
  const list = (Array.isArray(tasks) ? tasks : []).filter((t) => !t.yearRoundSchedule);
  const logoDataUrl = await loadDawsonRoadMaintenanceLogoDataUrl();

  let rangeStart = parseDateOnly(dateFrom) ?? startOfDay(new Date(dateFrom));
  let rangeEnd = parseDateOnly(dateTo) ?? startOfDay(new Date(dateTo));
  if (rangeStart.getTime() > rangeEnd.getTime()) {
    const tmp = rangeStart;
    rangeStart = rangeEnd;
    rangeEnd = tmp;
  }

  const sorted = list
    .map((t) => ({ t, dates: resolveTaskPrintDates(t) }))
    .filter((row): row is { t: ScheduleTaskForChart; dates: { startDate: Date | null; endDate: Date } } => row.dates != null)
    /** API date filter is broad; keep only tasks whose planned interval overlaps the selected range. */
    .filter(({ dates }) => plannedWindowOverlapsReportRange(dates, rangeStart, rangeEnd))
    .sort((a, b) => {
      const ak = (a.dates.startDate ?? a.dates.endDate).getTime();
      const bk = (b.dates.startDate ?? b.dates.endDate).getTime();
      return ak - bk;
    });

  const weekBeginning = `${fmtTableDate(rangeStart)} – ${fmtTableDate(rangeEnd)}`;
  const dateScheduled = fmtReportDate(generatedAt);

  const rowsHtml = sorted
    .map(({ t, dates }) => {
      const descRaw = String(t.description ?? '').trim();
      const nameRaw = String(t.name ?? '').trim();
      const desc = descRaw || nameRaw || '—';
      const taskNum = String(t.taskNumber ?? '').trim() || '—';
      const statusLabel = formatScheduleTaskStatusLabel(String(t.status ?? '')).trim() || '—';
      return `
        <tr>
          <td class="col-task">${escapeHtml(taskNum)}</td>
          <td class="col-desc">${escapeHtml(desc)}</td>
          <td class="col-date">${escapeHtml(fmtTableDate(dates.startDate))}</td>
          <td class="col-date">${escapeHtml(fmtTableDate(dates.endDate))}</td>
          <td class="col-status">${escapeHtml(statusLabel)}</td>
        </tr>`;
    })
    .join('');

  const emptyRow = `<tr><td colspan="5" class="text-center text-muted">No tasks for this foreman area and date range.</td></tr>`;

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Weekly Maintenance Schedule</title>
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        font-family: Arial, Helvetica, sans-serif;
        margin: 0;
        color: #111;
        font-size: 11px;
      }
      .drm-logo-wrap {
        text-align: right;
        margin-bottom: 6px;
      }
      .drm-logo {
        width: 120px;
        max-width: 45%;
        height: auto;
        object-fit: contain;
        display: inline-block;
      }
      .doc-title {
        text-align: center;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.02em;
        margin-bottom: 14px;
      }
      .meta-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 16px;
        font-size: 10px;
      }
      .meta-table td {
        border: 1px solid #333;
        padding: 6px 8px;
        vertical-align: top;
      }
      .meta-label {
        font-weight: 700;
        width: 22%;
        background: #f3f3f3;
      }
      .meta-value {
        width: 28%;
      }
      .data-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 10px;
      }
      .data-table th,
      .data-table td {
        border: 1px solid #333;
        padding: 5px 6px;
        vertical-align: top;
        word-wrap: break-word;
      }
      .data-table th {
        background: #e8e8e8;
        font-weight: 700;
        text-align: center;
      }
      .col-task { width: 14%; text-align: center; }
      .col-desc { width: 34%; }
      .col-date { width: 14%; text-align: center; white-space: nowrap; }
      .col-status { width: 14%; text-align: center; }
      .footer {
        margin-top: 18px;
        font-size: 8px;
        color: #444;
        border-top: 1px solid #999;
        padding-top: 8px;
      }
      .footer-note { margin-top: 4px; }
    </style>
  </head>
  <body>
    ${
      logoDataUrl
        ? `<div class="drm-logo-wrap"><img class="drm-logo" src="${logoDataUrl}" alt="Dawson Road Maintenance" /></div>`
        : ''
    }
    <div class="doc-title">WEEKLY MAINTENANCE SCHEDULE</div>

    <table class="meta-table" aria-label="Schedule header">
      <tr>
        <td class="meta-label">SERVICE AREA</td>
        <td class="meta-value">${escapeHtml(serviceAreaName || '—')}</td>
        <td class="meta-label">WEEK BEGINNING</td>
        <td class="meta-value">${escapeHtml(weekBeginning)}</td>
      </tr>
      <tr>
        <td class="meta-label">MANAGER AREA</td>
        <td class="meta-value">${escapeHtml(managerAreaName || '—')}</td>
        <td class="meta-label">SCHEDULED BY</td>
        <td class="meta-value">${escapeHtml(scheduledBy || '—')}</td>
      </tr>
      <tr>
        <td class="meta-label">SUPERVISOR AREA</td>
        <td class="meta-value"></td>
        <td class="meta-label">DATE SCHEDULED</td>
        <td class="meta-value">${escapeHtml(dateScheduled)}</td>
      </tr>
    </table>

    <table class="data-table" aria-label="Tasks">
      <thead>
        <tr>
          <th class="col-task">TASK #</th>
          <th class="col-desc">DESCRIPTION</th>
          <th class="col-date">START</th>
          <th class="col-date">COMPLETE</th>
          <th class="col-status">STATUS</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || emptyRow}
      </tbody>
    </table>

    <div class="footer">
      <div>Form QMS017 (Issue Date: 2019/06/01)</div>
      <div class="footer-note">
        Printed copies of this document are uncontrolled. Check master on intranet to ensure this printed copy is current.
      </div>
    </div>
    <script>
      (function () {
        function run() {
          setTimeout(function () { window.print(); }, 200);
        }
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run);
      })();
    </script>
  </body>
</html>`;

  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
}
